import { useEffect, useState } from 'react';
import { Badge, Card, ErrorMessage, Field, Loading, ModuleHeader, StatCard, Toggle, useAsync, useToast } from '../../components/ui';
import { IconAlert, IconCheck, IconMail, IconPlus, IconTrash } from '../../components/icons';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';

interface Person {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

/** Seitenkopf – als eigene Komponente, weil der Mandanten-Hinweis ihn ebenfalls trägt. */
function Kopf() {
  return (
    <ModuleHeader
      icon={<IconMail width={22} height={22} />}
      title="Benachrichtigungsmanager"
      subtitle="Wer den Auftragsbericht erhält – feste Adressen, ausgewählte Personen und dynamische Empfänger."
      pill="Konfiguration"
      tone="info"
    />
  );
}

/**
 * Empfänger des Auftragsberichts (Kap. 12).
 *
 * Drei Wege nebeneinander, weil sich die drei Gruppen unterschiedlich
 * verhalten: feste Adressen für Postfächer ohne Benutzerkonto, namentlich
 * gewählte Personen aus Disposition und Verwaltung, und die beiden
 * dynamischen Empfänger, die erst beim Abschluss feststehen.
 *
 * Namentlich statt rollenweit: Wer „alle Disponenten“ adressiert, überschüttet
 * auch die Urlaubsvertretung und den neuen Kollegen im ersten Monat.
 */
export function ReportingSettings() {
  const { notify } = useToast();
  const { user } = useAuth();
  const data = useAsync(() => api.get('/api/reporting/config'), []);
  const mails = useAsync(() => api.get('/api/reporting/mails'), []);

  const [form, setForm] = useState({
    enabled: false,
    manual: [] as string[],
    dispatcherIds: [] as number[],
    adminIds: [] as number[],
    includeAssignee: true,
    includeCustomer: false,
  });
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [testAddress, setTestAddress] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    if (user?.email && !testAddress) setTestAddress(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const sendTest = async () => {
    setTestBusy(true);
    setTestResult(null);
    try {
      const result = await api.post('/api/reporting/mails/test', { to: testAddress });
      setTestResult(result);
      notify(result.ok ? `Test-E-Mail an ${testAddress} versendet.` : `Versand fehlgeschlagen: ${result.error}`, result.ok ? 'success' : 'error');
    } catch (caught) {
      setTestResult({ ok: false, error: caught instanceof Error ? caught.message : String(caught) });
      notify('Der Versand konnte nicht angestoßen werden.', 'error');
    } finally {
      setTestBusy(false);
    }
  };

  useEffect(() => {
    if (data.data?.config) setForm({ ...form, ...data.data.config });
  }, [data.data]);

  if (data.loading && !data.data) return <Loading />;

  if (data.error) {
    return (
      <div className="stack">
        <Kopf />
        <div className="alert warning">
          <IconAlert width={16} height={16} />
          <span>
            Die Einstellungen konnten nicht geladen werden.
          </span>
        </div>
      </div>
    );
  }

  const candidates = data.data?.candidates ?? { dispatchers: [], admins: [] };
  const mail = data.data?.mail;

  const addManual = () => {
    const address = draft.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      notify('Bitte eine gültige E-Mail-Adresse eingeben.', 'error');
      return;
    }
    if (form.manual.includes(address)) {
      notify('Diese Adresse steht bereits in der Liste.', 'error');
      return;
    }
    setForm((current) => ({ ...current, manual: [...current.manual, address] }));
    setDraft('');
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put('/api/reporting/config', form);
      notify('Empfänger gespeichert.', 'success');
      data.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const total =
    form.manual.length +
    form.dispatcherIds.length +
    form.adminIds.length +
    (form.includeAssignee ? 1 : 0) +
    (form.includeCustomer ? 1 : 0);

  return (
    <div className="stack">
      <Kopf />

      <ErrorMessage error={error} />

      {!mail?.configured && (
        <div className="alert warning">
          <IconAlert width={16} height={16} />
          <span>
            Es ist kein SMTP-Zugang hinterlegt. Berichte werden erzeugt und sammeln sich im Postausgang, gehen aber
            noch nicht hinaus. Die Zugangsdaten gehören in <code>backend/.env</code> – nicht in diese Maske, damit sie
            nicht in der Datenbank landen.
          </span>
        </div>
      )}

      <div className="grid cols-4">
        <StatCard
          label="Versand nach Abschluss"
          value={form.enabled ? 'aktiv' : 'aus'}
          tone={form.enabled ? 'success' : 'warning'}
          icon={<IconMail width={17} height={17} />}
          hint={`${total} Empfänger konfiguriert`}
        />
        <StatCard
          label="Im Postausgang"
          value={mail?.pending ?? 0}
          tone={(mail?.pending ?? 0) > 0 ? 'warning' : 'success'}
          icon={<IconMail width={17} height={17} />}
          hint="warten auf Versand"
        />
        <StatCard label="Versendet" value={mail?.sent ?? 0} tone="info" icon={<IconCheck width={17} height={17} />} />
        <StatCard
          label="Gescheitert"
          value={mail?.failed ?? 0}
          tone={(mail?.failed ?? 0) > 0 ? 'danger' : 'success'}
          emphasis={(mail?.failed ?? 0) > 0 ? 'alarm' : undefined}
          icon={<IconAlert width={17} height={17} />}
        />
      </div>

      <Card title="Postausgang testen" subtitle="Prüft den SMTP-Zugang mit einer echten Nachricht, sofort und ohne Warteschlange">
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <input
            type="email"
            placeholder="name@unternehmen.de"
            value={testAddress}
            onChange={(event) => setTestAddress(event.target.value)}
            style={{ minWidth: 260 }}
          />
          <button
            className="btn primary"
            disabled={testBusy || !testAddress}
            onClick={sendTest}
          >
            <IconMail width={14} height={14} /> {testBusy ? 'Wird gesendet …' : 'Test-E-Mail senden'}
          </button>
          {testResult && (
            <Badge variant={testResult.ok ? 'success' : 'danger'}>
              {testResult.ok ? 'versendet' : 'fehlgeschlagen'}
            </Badge>
          )}
        </div>
        {testResult && !testResult.ok && testResult.error && (
          <div className="small subtle" style={{ marginTop: 8 }}>
            {testResult.error}
          </div>
        )}
      </Card>

      <Card title="Automatischer Versand">
        <div className="stack">
          <Toggle
            checked={form.enabled}
            onChange={(value) => setForm((current) => ({ ...current, enabled: value }))}
            label={
              <span>
                Bericht nach Abschluss automatisch versenden
                <span className="small subtle" style={{ display: 'block' }}>
                  Ausgeschaltet wird der Bericht trotzdem erzeugt und lässt sich am Auftrag herunterladen.
                </span>
              </span>
            }
          />
        </div>
      </Card>

      <div className="grid cols-2">
        <Card title="Feste Empfänger">
          <div className="stack">
            <span className="small subtle">
              Für Postfächer ohne Benutzerkonto – Rechnungswesen, Archiv, Objektverwaltung.
            </span>
            <div className="row" style={{ gap: 8 }}>
              <input
                type="email"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addManual();
                  }
                }}
                placeholder="berichte@auftraggeber.de"
              />
              <button className="btn sm" onClick={addManual} disabled={!draft.trim()}>
                <IconPlus width={13} height={13} /> Hinzufügen
              </button>
            </div>

            {form.manual.length === 0 ? (
              <span className="small subtle">Keine feste Adresse hinterlegt.</span>
            ) : (
              <div className="stack" style={{ gap: 5 }}>
                {form.manual.map((address) => (
                  <div key={address} className="row between">
                    <span className="mono small">{address}</span>
                    <button
                      className="btn sm ghost"
                      onClick={() =>
                        setForm((current) => ({ ...current, manual: current.manual.filter((a) => a !== address) }))
                      }
                      title="Entfernen"
                    >
                      <IconTrash width={13} height={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card title="Dynamische Empfänger">
          <div className="stack">
            <span className="small subtle">
              Diese beiden stehen erst beim Abschluss fest und werden je Auftrag ermittelt.
            </span>
            <Toggle
              checked={form.includeAssignee}
              onChange={(value) => setForm((current) => ({ ...current, includeAssignee: value }))}
              label={
                <span>
                  Zuständiger Mitarbeiter
                  <span className="small subtle" style={{ display: 'block' }}>
                    Die Person, die den Auftrag beim Abschluss zugewiesen hatte.
                  </span>
                </span>
              }
            />
            <Toggle
              checked={form.includeCustomer}
              onChange={(value) => setForm((current) => ({ ...current, includeCustomer: value }))}
              label={
                <span>
                  Eigentümer/Verwalter
                  <span className="small subtle" style={{ display: 'block' }}>
                    Die beim Eigentümer/Verwalter des Objekts hinterlegte Kontaktadresse.
                  </span>
                </span>
              }
            />
          </div>
        </Card>
      </div>

      <div className="grid cols-2">
        <PersonPicker
          title="Disponenten"
          hint="Mehrfachauswahl – nur wer hier steht, bekommt den Bericht."
          people={candidates.dispatchers}
          selected={form.dispatcherIds}
          onChange={(ids) => setForm((current) => ({ ...current, dispatcherIds: ids }))}
        />
        <PersonPicker
          title="Administratoren"
          hint="Mehrfachauswahl aus Verwaltung und Systemadministration."
          people={candidates.admins}
          selected={form.adminIds}
          onChange={(ids) => setForm((current) => ({ ...current, adminIds: ids }))}
        />
      </div>

      <div className="row">
        <button className="btn primary" onClick={save} disabled={busy}>
          Empfänger speichern
        </button>
        <span className="spacer" />
        <button
          className="btn"
          onClick={async () => {
            const result = await api.post('/api/reporting/mails/flush');
            notify(
              result.skipped
                ? `${result.skipped} Nachricht(en) warten weiter – kein SMTP-Zugang hinterlegt.`
                : `${result.sent} versendet, ${result.failed} fehlgeschlagen.`,
              result.failed ? 'error' : 'success',
            );
            mails.reload();
            data.reload();
          }}
        >
          Postausgang jetzt leeren
        </button>
      </div>

      <Card title="Postausgang" noBody>
        {mails.loading ? (
          <Loading />
        ) : (mails.data?.items ?? []).length === 0 ? (
          <div className="card-body">
            <span className="small subtle">Es wurde noch keine Nachricht erzeugt.</span>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Erstellt</th>
                  <th>Betreff</th>
                  <th>Empfänger</th>
                  <th>Anhang</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(mails.data?.items ?? []).map((entry: any) => {
                  const recipients = safeList(entry.recipients);
                  return (
                    <tr key={entry.id}>
                      <td className="small subtle">{formatDateTime(entry.created_at)}</td>
                      <td className="small">{entry.subject}</td>
                      <td className="small subtle" title={recipients.join(', ')}>
                        {recipients.length === 1 ? recipients[0] : `${recipients.length} Empfänger`}
                      </td>
                      <td className="small subtle">{entry.attachment_name ?? '–'}</td>
                      <td>
                        <Badge
                          variant={
                            entry.status === 'sent' ? 'success' : entry.status === 'failed' ? 'danger' : 'warning'
                          }
                        >
                          {{ pending: 'wartet', sent: 'versendet', failed: 'gescheitert' }[entry.status as string] ??
                            entry.status}
                        </Badge>
                        {entry.last_error && (
                          <div className="small subtle truncate" style={{ maxWidth: 260 }} title={entry.last_error}>
                            {entry.last_error}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Mehrfachauswahl von Personen.
 *
 * Kein `<select multiple>`: Das verlangt Strg-Klick, verliert bei einem
 * Fehlklick die ganze Auswahl und zeigt bei mehr als sechs Einträgen nichts
 * Brauchbares. Kästchen mit Namen und Adresse sind länger, aber man sieht,
 * was gewählt ist.
 */
function PersonPicker({
  title,
  hint,
  people,
  selected,
  onChange,
}: {
  title: string;
  hint: string;
  people: Person[];
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const visible = needle
    ? people.filter((person) =>
        `${person.first_name} ${person.last_name} ${person.email}`.toLowerCase().includes(needle),
      )
    : people;

  return (
    <Card title={`${title}${selected.length ? ` (${selected.length})` : ''}`}>
      <div className="stack" style={{ gap: 8 }}>
        <span className="small subtle">{hint}</span>
        {people.length > 6 && (
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Person suchen …"
          />
        )}
        {people.length === 0 ? (
          <span className="small subtle">Keine Person mit dieser Rolle und hinterlegter E-Mail-Adresse.</span>
        ) : (
          <div className="stack" style={{ gap: 3, maxHeight: 220, overflowY: 'auto' }}>
            {visible.map((person) => (
              <label key={person.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={selected.includes(person.id)}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...selected, person.id]
                        : selected.filter((id) => id !== person.id),
                    )
                  }
                />
                <span>
                  {person.first_name} {person.last_name}
                  <span className="small subtle" style={{ display: 'block' }}>
                    {person.email}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function safeList(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
